import { createSelector } from "reselect";
import {
	getFormSyncWarnings,
	getFormMeta,
	getFormValues,
	getFormSyncErrors
} from "redux-form";
import { merge } from "lodash";

import {
	buildImagePlacements,
	buildVideoPlacements,
	buildCarouselPlacements,
	buildCarouselPreviewTabs,
	buildVideoPreviewTabs,
	buildImagePreviewTabs
} from "selectors/Social";
import { PAC_FORM_TYPES, PLATFORM_BY_DSP_INTERNAL_NAME, GOOGLE_ASPECT_RATIO_LABELS } from "social/constants/common";
import { PLATFORM_BY_ADTYPE, SOCIAL_AD_TYPES } from "utils/constants";
import { SOCIAL_WIZARD } from "widgets/forms/list";
import { isSocialCarouselCreative } from "utils/common";

export function isWizardValid(state){
	/*
		When Fields with errors are unmounted, the `isValid` redux-form selector doesn't actually work,
		and will give false positives, even though there are errored fields in the form's `syncErrors`.

		e.g. If there's an error for a FacebookImage's `name` field (missing, too long, etc), but then you
		switch to the FacebookVideo section of the form, `isValid` will return `true`, in spite of the
		error existing in the store.

		This selector just counts the keys in syncErrors and asyncErrors, and returns `true` if the count is 0.

		Note: The actual error count may be much higher than this, because of nesting, but if it's clear of
		errors, there should be no keys in these parts of the store.
	*/
	const errors = [
		getFormSyncErrors(SOCIAL_WIZARD)(state),
		/*
		TODO: There's a fringe case where a concept with the same name could be created while in the
		Add Content step of the wizard (different user, browser tab, etc.), which we'd want to invalidate
		submission for, until they update the concept name.

		We currently don't display async validation errors in the error banners, though, so we don't want to
		disable submit for no apparent reason to the user.
		*/
		//getFormAsyncErrors(SOCIAL_WIZARD)(state),
	];

	const numErrors = errors.reduce((prev, item = {}) => {
		return prev + Object.keys(item).length;
	}, 0);

	return (numErrors === 0);
}

export function getFormStateByAdType(state, adType) {
	const meta = getFormMeta(SOCIAL_WIZARD)(state) || {};
	const values = getFormValues(SOCIAL_WIZARD)(state) || {};
	const warnings = getFormSyncWarnings(SOCIAL_WIZARD)(state) || {};
	const syncErrors = getFormSyncErrors(SOCIAL_WIZARD)(state) || {};

	return {
		meta: { ...meta[adType] },
		values: { ...values[adType] },
		warnings: { ...warnings[adType] },
		errors: { ...syncErrors[adType] },
	};
}

export function getErrorMessagesByAdType(adType, showPristineErrors) {
	/*
		`showPristineErrors` can be passed in as a boolean, or an array.

		If it's an array, then it will be interpreted to mean the list of keys where we show pristine errors
		i.e. It will behave as if `showPristineErrors` were false, except for the items in the array.
	*/
	return (state) => {
		/*
			Warnings and errors are keyed by field and holds one
			e.g.
				warnings: {
					headline: "Field exceeds max of 200 characters",
					name: "Name already exists"
				}
			We will combine these into one mapping, ignoring pristine fields. We cannot soley rely on touched,
			because some fields are updated via the shared content form.

			For now, we allow warnings to overwrite errors to match Rhombus. E.g. We have both a warning and
			an error for the landingPage. The warning message would show instead of the error message.
		*/
		function messageReducer(warnings, values = {}, meta = {}, init = {}) {
			return Object.keys(warnings).reduce((prev, key) => {
				if (typeof warnings[key] === "object") {
					/*
						To account for deeply nested fields, we use recursion to drill down to the bottommost
						object whose key, value pairs will be in the form of `[fieldName]: [message]`
					*/
					const messages = messageReducer(warnings[key], values[key], meta[key]);
					prev[key] = messages;
					return prev;
				}

				const { pristine, touched } = meta[key] || {};
				const showPristineForKey = (Array.isArray(showPristineErrors)) ? showPristineErrors.includes(key) : showPristineErrors;

				/*
					Some fields can be updated by the shared content form. These fields will not be touched,
					but they will have a value that is not pristine.
				*/
				if (showPristineForKey || (values[key] && !pristine) || touched) {
					prev[key] = warnings[key];
				}

				return prev;
			}, init);
		}

		const { meta, values, warnings, errors } = getFormStateByAdType(state, adType);
		const warningMessages = messageReducer(warnings, values, meta, {});
		const errorMessages = messageReducer(errors, values, meta, {});

		/*
			Lodash's merge will properly combine nested messages with the second param receiving priority e.g.

			warningsMessages = {
				image: "Field is required",
				placements: {
					Square: { image: "Field is required "}
				}
			}
			errorMessages = {
				placements: {
					Square: { headline: "Field is too long "}
				}
			}

			merges as

			result = {
				image: "Field is required",
				placements: {
					Square: {
						image: "Field is required",
						headline: "Field is too long",
					}
				}
			}
		*/
		return merge(errorMessages, warningMessages);
	};
}

export function getPlacementErrorByAdType(adType, placementType, showPristineErrors) {
	return (state) => {
		let errorMessages = getErrorMessagesByAdType(adType, showPristineErrors)(state);

		if (placementType === PAC_FORM_TYPES.DEFAULT) {
			delete errorMessages.placements;
		} else {
			errorMessages = { ...errorMessages.placements?.[placementType] };
		}

		return {
			errorMessages,
			hasError: Boolean(Object.keys(errorMessages).length),
		};
	};
}

export function getCurrentFieldNames(state) {
	const { registeredFields = {} } = state.form[SOCIAL_WIZARD] || {};
	return Object.keys(registeredFields).filter(key => registeredFields[key]?.count);
}

function isAdTypeTouched(meta, adType){
	return Object.values(meta[adType] || []).some(field => field.touched);
}

export function getSelectedAdTypesWithPlatforms(state){
	const values = getFormValues(SOCIAL_WIZARD)(state);
	const adTypes = values?.adTypes || [];

	return adTypes.map((adType) => {
		return {
			platform: PLATFORM_BY_ADTYPE[adType],
			adType,
		};
	});
}

export function getValidationByAdType(state){
	const errors = getFormSyncErrors(SOCIAL_WIZARD)(state);
	const warnings = getFormSyncWarnings(SOCIAL_WIZARD)(state);
	const meta = getFormMeta(SOCIAL_WIZARD)(state);
	const values = getFormValues(SOCIAL_WIZARD)(state);

	const adTypes = values?.adTypes || [];

	return adTypes.reduce((prev, adType) => {
		const isTouched = isAdTypeTouched(meta, adType);
		const hasError = errors[adType];
		const hasWarning = warnings[adType];

		const hasProblem = hasError || hasWarning;

		prev[adType] = {
			isValid: isTouched ? !hasProblem : undefined,
		};
		return prev;
	}, {});
}

export function getValidationByPlatform(state){
	const errors = getFormSyncErrors(SOCIAL_WIZARD)(state);
	const warnings = getFormSyncWarnings(SOCIAL_WIZARD)(state);
	const meta = getFormMeta(SOCIAL_WIZARD)(state);
	const values = getFormValues(SOCIAL_WIZARD)(state);

	const adTypes = values?.adTypes || [];

	const platforms = new Set(adTypes.map(item => PLATFORM_BY_ADTYPE[item]));

	return [...platforms].reduce((prev, platform) => {
		const platformAdTypes = adTypes.filter(adType => PLATFORM_BY_ADTYPE[adType] === platform);
		const anyError = platformAdTypes.some(adType => errors[adType]);
		const anyWarning = platformAdTypes.some(adType => warnings[adType]);
		const anyTouched = platformAdTypes.some(adType => isAdTypeTouched(meta, adType));
		const everyTouched = platformAdTypes.every(adType => isAdTypeTouched(meta, adType));

		const anyProblem = anyError || anyWarning;

		/*
			If any of the forms are touched, and there's a warning or error, we show a warning icon (isValid = false).
			Only if all forms are touched, and they're all valid, do we show a success icon (isValid = true).
			Otherwise, show no icon (isValid = undefined).
		*/
		let isValid;
		if(anyTouched && anyProblem){
			isValid = false;
		} else if(everyTouched && !anyProblem){
			isValid = true;
		}

		prev[platform] = { isValid };
		return prev;
	}, {});
}

function getDSP(state){
	return state.social?.items || [];
}

export function compileConnectedPlatforms(dsps) {
	if (!dsps.length) { return []; }

	let platforms = new Set();

	dsps.forEach((item) => {
		const platform = PLATFORM_BY_DSP_INTERNAL_NAME[item.dsp.internalName];
		if (platform) { platforms.add(platform); }
	});

	return Array.from(platforms);
}

export const getConnectedPlatforms = createSelector([getDSP], compileConnectedPlatforms);

/* Only looking for facebook images or videos, but likely this will expand and rename.
moved from FacebookPreview.js helper fn for selecting FT type facebook previews
*/
export function facebookPreviewSelector(socialAdType){
	if(socialAdType === SOCIAL_AD_TYPES.FACEBOOK_IMAGE){
		return buildImagePlacements;
	}else if(isSocialCarouselCreative({ adType: socialAdType })) {
		return buildCarouselPlacements;
	}

	return buildVideoPlacements;
}

export function facebookPreviewAdTypes(adType) {
	switch(adType) {
	case SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL:
		return buildCarouselPreviewTabs;
	case SOCIAL_AD_TYPES.FACEBOOK_IMAGE:
		return buildImagePreviewTabs;
	case SOCIAL_AD_TYPES.FACEBOOK_VIDEO:
		return buildVideoPreviewTabs;
	default:
		return () => ['facebook', 'instagram', 'messenger'];
	}
}

function getGoogleLocales(state){
	return state.social.googleLocales.locales || {};
}

function buildGoogleLanguageOptions(locales){
	return Object.entries(locales)?.map(([key, locale]) => ({
		id: key,
		name: locale.name,
		value: key,
		label: locale.name,
	}));
}

export const getGoogleLanguageOptions = createSelector([getGoogleLocales], buildGoogleLanguageOptions);

function getAspectRatios(state) {
	const { adType = "", socialValues: { aspectRatios = []} } = state?.creativeDetails || {};
	return { adType, aspectRatios };
}

function buildPlacementSelectorOptions(aspectRatioData){
	const { adType = "", aspectRatios = []} = aspectRatioData || {};
	const labelOrder = { square: 1, horizontal: 2, vertical: 3 };

	let aspectRatioLabels = aspectRatios.map((ratio) => {
		return { value: ratio, label: GOOGLE_ASPECT_RATIO_LABELS[adType][ratio] };
	});

	return aspectRatioLabels.sort((curr, next) => {
		return labelOrder[curr.value] - labelOrder[next.value];
	});
}

export const getGoogleAspectRatios = createSelector([getAspectRatios], buildPlacementSelectorOptions);

function getConceptAdTypes(state){
	return state.form[SOCIAL_WIZARD].values?.adTypes || [];
}

function getSocialState(state){
	return state.social;
}

function buildDynamicCTAs(adTypes, socialState = {}){
	const adTypeSet = new Set(adTypes);
	const ctasByAdType = {};
	const googleCTANames = socialState.googleLocales?.locales?.en?.ctas?.map(cta => cta.name) || [];

	if(adTypeSet.has(SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_CAROUSEL)){
		ctasByAdType[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_CAROUSEL] = googleCTANames;
	}
	if(adTypeSet.has(SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_IMAGE)){
		ctasByAdType[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_IMAGE] = googleCTANames;
	}

	return ctasByAdType;
}

export const getDynamicCTAs = createSelector([getConceptAdTypes, getSocialState], buildDynamicCTAs);
